/**
 * Fanatic team switcher (AC-FANATIC-004).
 *
 * A Fanatic can hold grants on many teams at once, so the portal header carries
 * a switcher rather than a fixed team badge. It is a listbox-flavoured menu
 * button: click or `Enter`/`Space` opens it, `ArrowUp`/`ArrowDown` move,
 * `Escape` closes and returns focus, and a click outside dismisses it.
 *
 * Pending invites and revoked history are listed but not selectable — only an
 * `active` grant unlocks data, and saying so beats hiding the row.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { EmptyState } from '../../components/empty-state';
import type { FanaticGrant, FanaticGrantStatus } from '../../lib/api-types';

/** Above this many followed teams the menu grows a filter box. */
const SEARCH_THRESHOLD = 5;

const STATUS_LABEL: Record<FanaticGrantStatus, string> = {
  active: 'Following',
  pending_invite: 'Invite pending',
  revoked: 'No longer following',
};

const STATUS_CLASS: Record<FanaticGrantStatus, string> = {
  active: 'fan-badge fan-badge--active',
  pending_invite: 'fan-badge fan-badge--pending',
  revoked: 'fan-badge fan-badge--revoked',
};

export interface TeamSwitcherProps {
  grants: readonly FanaticGrant[];
  activeGrant: FanaticGrant | null;
  isLoading: boolean;
  onSelect: (grant: FanaticGrant) => void;
}

function teamLabel(grant: FanaticGrant): string {
  return grant.team_name ?? grant.team_slug ?? 'Unnamed team';
}

export function TeamSwitcher({
  grants,
  activeGrant,
  isLoading,
  onSelect,
}: TeamSwitcherProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const ordered = useMemo(() => {
    const rank: Record<FanaticGrantStatus, number> = {
      active: 0,
      pending_invite: 1,
      revoked: 2,
    };
    return [...grants].sort(
      (left, right) =>
        rank[left.status] - rank[right.status] ||
        teamLabel(left).localeCompare(teamLabel(right), undefined, { sensitivity: 'base' })
    );
  }, [grants]);

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (needle === '') return ordered;
    return ordered.filter((grant) =>
      `${teamLabel(grant)} ${grant.team_slug ?? ''}`.toLowerCase().includes(needle)
    );
  }, [filter, ordered]);

  const activeCount = ordered.filter((grant) => grant.status === 'active').length;

  const close = useCallback(() => {
    setIsOpen(false);
    setFilter('');
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (event: MouseEvent): void => {
      if (containerRef.current?.contains(event.target as Node) === false) close();
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        close();
        triggerRef.current?.focus();
      }
    };
    globalThis.document?.addEventListener('mousedown', onPointerDown);
    globalThis.document?.addEventListener('keydown', onKeyDown);
    return () => {
      globalThis.document?.removeEventListener('mousedown', onPointerDown);
      globalThis.document?.removeEventListener('keydown', onKeyDown);
    };
  }, [close, isOpen]);

  const moveFocus = (fromIndex: number, delta: number): void => {
    const options =
      containerRef.current?.querySelectorAll<HTMLButtonElement>('.team-switcher__option');
    if (options === undefined || options.length === 0) return;
    const next = (fromIndex + delta + options.length) % options.length;
    options[next]?.focus();
  };

  return (
    <div className="team-switcher" ref={containerRef}>
      <button
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className="team-switcher__trigger"
        disabled={isLoading && ordered.length === 0}
        onClick={() => (isOpen ? close() : setIsOpen(true))}
        ref={triggerRef}
        type="button"
      >
        <span className="team-switcher__label">Viewing team</span>
        <span className="team-switcher__name">
          {isLoading && activeGrant === null
            ? 'Loading…'
            : activeGrant === null
              ? 'No team selected'
              : teamLabel(activeGrant)}
          <span className="team-switcher__count">
            ({activeCount} following{isOpen ? '' : ' ▾'})
          </span>
        </span>
      </button>

      {isOpen ? (
        <div className="team-switcher__menu" role="dialog" aria-label="Switch team">
          {ordered.length > SEARCH_THRESHOLD ? (
            <input
              aria-label="Filter followed teams"
              className="team-switcher__search"
              onChange={(event) => setFilter(event.target.value)}
              placeholder="Filter teams…"
              type="search"
              value={filter}
            />
          ) : null}

          {visible.length === 0 ? (
            ordered.length === 0 ? (
              <EmptyState
                isCompact
                className="team-switcher__empty"
                title="No teams followed yet"
                description="Browse teams to follow one — its games appear here."
              />
            ) : (
              <EmptyState
                isCompact
                variant="filtered"
                className="team-switcher__empty"
                title="No followed team matches that filter"
                description="Try a shorter team name."
              />
            )
          ) : (
            <ul className="team-switcher__options" role="listbox">
              {visible.map((grant, index) => {
                const isActive = grant.tenant_id === activeGrant?.tenant_id;
                const isSelectable = grant.status === 'active';
                return (
                  <li key={grant.grant_id}>
                    <button
                      aria-selected={isActive}
                      className={`team-switcher__option${
                        isActive ? ' team-switcher__option--active' : ''
                      }`}
                      disabled={!isSelectable}
                      onClick={() => {
                        onSelect(grant);
                        close();
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'ArrowDown') {
                          event.preventDefault();
                          moveFocus(index, 1);
                        }
                        if (event.key === 'ArrowUp') {
                          event.preventDefault();
                          moveFocus(index, -1);
                        }
                      }}
                      role="option"
                      type="button"
                    >
                      <span className="team-switcher__option-main">
                        <span>{teamLabel(grant)}</span>
                        <span className="team-switcher__meta">
                          {grant.team_slug ?? '—'}
                          {grant.status === 'pending_invite'
                            ? ' · accept the emailed invite to view stats'
                            : ''}
                        </span>
                      </span>
                      <span className={STATUS_CLASS[grant.status]}>
                        {STATUS_LABEL[grant.status]}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
